import React from 'react';
import PropTypes from 'prop-types';
import styles from './ProductsListView.module.scss';
import Checkbox from '../../common/Checkbox';
import { formatMany } from '../../../utils/formatHelper';
import BootstrapTable from 'react-bootstrap-table-next';
import { sortCaret } from '../../../utils/tableHelper';
import { ApprovedStatus } from '../../../constants/approvedStatus';
import { useMemo } from 'react';
import { useCallback } from 'react';
import cn from 'classnames';
import InventoryTableHeaderWithTooltip from '../../tables/pricing/inventory/invetory-table-header-with-tooltip';

function ProductsListView(props) {
  const { setOrder, orderBy, order, approvedStatus, isChecked, onChecked, shouldAllowUserToDoActions } = props;

  const formatCheckbox = useCallback((cell, row, rowIndex, { isChecked, onChecked }) => {
    const { model } = row;
    const payload = {
      model,
      variants: row.variants,
    };

    return (
      <div className={styles.checkboxWrapper}>
        <Checkbox
          checked={isChecked(model)}
          value={model}
          onChange={(checked) => onChecked(checked, payload)}
          hideLabel
          inline
        />
      </div>
    );
  }, []);

  const formatImage = useCallback((cell, row) => {
    if (cell.length === 0) {
      return null;
    }

    return <img src={cell[0]} alt={row.model} className={styles.image} />;
  }, []);

  const formatInventory = useCallback((cell, row) => {
    return (
      <span>
        {cell} ({formatMany(row.variants.length, 'variant', true)})
      </span>
    );
  }, []);

  const onSortChange = useCallback(
    (newOrderBy, newOrder) => {
      if (newOrderBy !== orderBy || newOrder !== order) {
        setOrder(newOrder, newOrderBy);
      }
    },
    [setOrder, orderBy, order],
  );

  const statusFormatter = useCallback(
    (cell, row) => {
      const status = approvedStatus(row.variants);
      switch (status) {
        case ApprovedStatus.NOT_APPROVED:
          return 'Not Approved';

        case ApprovedStatus.PENDING:
          return 'Pending';

        case ApprovedStatus.SYNCED:
          return 'Synced';

        default:
          return '';
      }
    },
    [approvedStatus],
  );

  const selectRowOptions = {
    hideSelectColumn: true,
    mode: 'radio',
    clickToSelect: true,
    onSelect: (row, selected, rowIndex, e) => {
      const checkboxWrapper = e.target.closest(`.${styles.checkboxWrapper}`);
      if (!checkboxWrapper) {
        props.open(row);
      }

      return false;
    },
  };

  const noDataIndication = () => `There is no data to display`;

  const columns = useMemo(() => {
    const columns = [];
    if (shouldAllowUserToDoActions) {
      columns.push({
        text: '',
        dataField: 'id',
        isDummyField: true,
        formatter: formatCheckbox,
        formatExtraData: {
          isChecked,
          onChecked,
        },
        classes: 'narrow',
        headerClasses: 'narrow',
      });
    }
    columns.push(
      {
        text: 'Image',
        dataField: 'square_images',
        classes: cn(styles.imageWrapper, styles.clickable),
        formatter: formatImage,
      },
      {
        text: 'Model',
        dataField: 'model',
        sort: true,
        onSort: onSortChange,
        classes: styles.clickable,
      },
      {
        text: 'Title',
        dataField: 'title',
        sort: true,
        onSort: onSortChange,
        classes: styles.clickable,
      },
      {
        text: 'Units In Cymbio',
        dataField: 'available_inventory',
        formatter: formatInventory,
        classes: styles.clickable,
        headerFormatter: () => (
          <InventoryTableHeaderWithTooltip
            text="Units In Cymbio"
            tooltip="Reflects only enabled warehouses for this retailer"
          />
        ),
      },
    );

    columns.push({
      text: 'Status',
      dataField: 'status',
      isDummyField: true,
      formatter: statusFormatter,
      classes: styles.clickable,
    });

    return columns;
  }, [
    isChecked,
    onChecked,
    onSortChange,
    statusFormatter,
    formatCheckbox,
    formatImage,
    formatInventory,
    shouldAllowUserToDoActions,
  ]);

  return (
    <BootstrapTable
      data={props.products}
      columns={columns}
      keyField="model"
      printable
      striped
      hover
      bordered={false}
      selectRow={selectRowOptions}
      noDataIndication={noDataIndication}
      defaultSorted={[
        {
          dataField: props.orderBy,
          order: props.order,
        },
      ]}
      defaultSortDirection={props.order || 'desc'}
      sort={{
        sortCaret: sortCaret,
      }}
    />
  );
}

export default ProductsListView;

ProductsListView.defaultProps = {
  order: 'asc',
  orderBy: 'title',
};

ProductsListView.propTypes = {
  products: PropTypes.array.isRequired,
  order: PropTypes.string,
  orderBy: PropTypes.string,
  setOrder: PropTypes.func.isRequired,
  approvedStatus: PropTypes.func.isRequired,
  open: PropTypes.func.isRequired,
  isChecked: PropTypes.func.isRequired,
  onChecked: PropTypes.func.isRequired,
  shouldAllowUserToDoActions: PropTypes.bool.isRequired,
};
